import React from 'react';
import App from 'next/app';
import Head from 'next/head';
import { getParser } from 'bowser';
import { Provider } from 'mobx-react';
import { parseDocumentCookie } from './api/cookies';
import {
  stores,
  initStores,
  getSnapshots,
  AdaStoreContext,
} from '@studionand/ada-next/src/stores';
import I18n from '@studionand/ada-next/src/i18n';
import lngDict from '@studionand/ada-next/src/i18n/locales/de';
import getInitialCookieVerification from '@studionand/ada-next/src/utils/getInitialCookieVerification';
import AppWrapper from './components/AppWrapper';
import { getStoreInstances } from './stores/index';
import { handleRedirectToNotSupportedPage } from './utils/checkBrowserSupport';

if (global.window) {
  window.getParser = getParser;
}

const createContextualizedApp = getAppContext => {
  class MyApp extends App {
    static async getInitialProps({ Component, ctx }) {
      const { dataStore, uiStore } = getStoreInstances();
      const isServer = !!ctx.req;
      initStores(isServer);
      if (isServer) {
        uiStore?.setFromCookies(ctx.req.cookies);
      }
      await getInitialCookieVerification(ctx);
      const appContext = getAppContext(ctx.pathname);
      uiStore.setContext(appContext);
      uiStore.setFromQueryParameters(ctx.query);
      if (isServer)
        handleRedirectToNotSupportedPage(ctx.req, ctx.res, appContext);
      let pageProps = {};
      stores.appStore.instance.setPageIsEditable(false);
      if (Component.getInitialProps) {
        pageProps = await Component.getInitialProps({
          dataStore,
          uiStore,
          adaStores: {
            appStore: stores.appStore.instance,
          },
          ...ctx,
        });
      }
      return {
        isServer,
        pageProps,
        pathname: ctx.pathname,
        asPath: ctx.asPath,
        query: ctx.query,
        dataStore: dataStore.getSnapshot(),
        uiStore: uiStore.getSnapshot(),
        adaStores: getSnapshots(),
      };
    }
    constructor(props) {
      super(props);
      this.adaStores = initStores(props.isServer, props.adaStores);
      const { dataStore, uiStore } = getStoreInstances();
      this.dataStore = dataStore;
      this.uiStore = uiStore;
      this.dataStore?.hydrate(props.dataStore);
      this.uiStore?.hydrate(props.uiStore);
      if (global.window) {
        const cookies = parseDocumentCookie(document.cookie);
        this.uiStore?.setFromCookies(cookies);
      }
    }

    get adaStoresAsProps() {
      return Object.keys(this.adaStores).reduce((acc, key) => {
        acc[key] = this.adaStores[key].instance;
        return acc;
      }, {});
    }

    componentDidMount() {
      // Remove the server-side injected CSS.
      const jssStyles = document.querySelector('#jss-server-side');
      if (jssStyles) {
        jssStyles.parentNode.removeChild(jssStyles);
      }
    }

    render() {
      const { Component, pageProps } = this.props;
      return (
        <React.Fragment>
          <I18n lngDict={lngDict} locale="de">
            <Provider
              dataStore={this.dataStore}
              uiStore={this.uiStore}
              {...this.adaStoresAsProps}
            >
              <AdaStoreContext.Provider value={{ ...this.adaStoresAsProps }}>
                <AppWrapper>
                  <Component {...pageProps} context={this.uiStore.context} />
                </AppWrapper>
              </AdaStoreContext.Provider>
            </Provider>
          </I18n>
        </React.Fragment>
      );
    }
  }
  return MyApp;
};

export default createContextualizedApp;
